import {
  useRef,
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react'
import { createLogger } from 'utils/logger'
import WaveSurfer from 'wavesurfer.js'
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js'
import { getWaveBlob } from 'webm-to-wav-converter'

// 훅이 반환할 타입 정의 (추가된 기능 포함)
export type VisualizerMode = 'idle' | 'recording' | 'playing_recorded'

interface UseWaveSurferVisualizerResult {
  canvasRef: React.RefObject<HTMLDivElement>
  mode: VisualizerMode
  startRecording: () => Promise<void> // 녹음 시작 함수
  stopRecording: () => void // 녹음 중지 함수
  recordedAudioUrl: string | null // 녹음된 오디오 URL
  toggleRecordedPlayback: () => void // 녹음된 오디오 재생/일시정지 함수
  isPlayingRecorded: boolean // 녹음된 오디오 재생 중인지 여부
  currentTime: number // 현재 재생 시간
  duration: number // 오디오 총 길이
  formatTime: (seconds: number) => string // 시간 포맷팅 헬퍼 함수
}

const logger = createLogger('waveSurferComponent')
export const useWaveSurferVisualizer = (
  setFile: Dispatch<SetStateAction<File | null>>,
  setFileDuration: Dispatch<SetStateAction<number | null>>
): UseWaveSurferVisualizerResult => {
  const waveSurferRef = useRef<WaveSurfer | null>(null)
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const recordPluginRef = useRef<RecordPlugin | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null) // setInterval ID를 저장할 ref

  const [mode, setMode] = useState<VisualizerMode>('idle')
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null)
  const [isPlayingRecorded, setIsPlayingRecorded] = useState<boolean>(false)
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)

  // 타이머를 정리하는 함수
  const cleanupInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])
  const setupWaveSurfer = useCallback(
    (
      container: HTMLElement | ShadowRoot,
      isLiveMode: boolean,
      audioUrlToLoad: string | null = null
    ) => {
      logger.info(
        `Setting up WaveSurfer in ${isLiveMode ? 'Live' : 'Playback'} Mode...`
      )

      // 기존 WaveSurfer 인스턴스 파괴 (새로운 모드/오디오 로드를 위해)
      if (waveSurferRef.current) {
        waveSurferRef.current.destroy()
        waveSurferRef.current = null
        logger.info('Destroyed previous WaveSurfer instance.')
      }

      // RecordPlugin 인스턴스 생성 (아직 없다면)
      if (!recordPluginRef.current) {
        recordPluginRef.current = RecordPlugin.create({
          mimeType: 'audio/webm;codecs=opus',
          renderRecordedAudio: true,
          scrollingWaveform: true,
        })
        logger.info('RecordPlugin created.')

        // RecordPlugin 전용 이벤트 리스너는 여기서 한 번만 부착합니다.
        recordPluginRef.current.on('record-end', async (blob: Blob) => {
          logger.info(
            'Record-end event fired (Recording finished). Blob size:',
            { size: blob.size }
          )

          try {
            // 📌 변환: webm Blob → wav Blob
            const wavBlob = await getWaveBlob(blob, false) // false = 16bit wav
            logger.info('wavBlob', wavBlob)

            // 📁 wav File 생성
            const wavFile = new File([wavBlob], 'recording.wav', {
              type: 'audio/wav',
              lastModified: Date.now(),
            })

            setFile(wavFile)
            // 🔗 미리듣기용 URL 생성
            const url = URL.createObjectURL(wavBlob)
            setRecordedAudioUrl(url) // useEffect 트리거
            setMode('playing_recorded') // useEffect 트리거

            logger.info('녹음 완료. WAV URL:', { url: url })
          } catch (error) {
            logger.error(JSON.stringify(error))
          }
        })
        recordPluginRef.current.on('record-progress', (time: number) => {
          logger.info('record-progress', { time: time })
        })
      }

      // ⭐⭐⭐ 핵심 수정 부분입니다 ⭐⭐⭐
      const containerElement = canvasRef.current
      let actualContainer: HTMLElement | ShadowRoot | null = null

      if (containerElement) {
        // 스크린샷에 따르면, canvasRef의 첫 번째 자식 요소(div)가 섀도우 루트를 가지고 있습니다.
        const shadowHostElement =
          containerElement.firstElementChild as HTMLElement | null

        if (shadowHostElement && shadowHostElement.shadowRoot) {
          actualContainer = shadowHostElement.shadowRoot
          logger.info(
            'Using Shadow DOM (nested) as container:',
            actualContainer
          )
        } else {
          // 만약 자식 요소에 섀도우 루트가 없거나, 자식 요소가 없다면, 원래 컨테이너를 사용합니다.
          // (이 경우는 섀도우 돔 환경이 아니라고 가정)
          actualContainer = containerElement
          logger.info('Using Light DOM element as container:', actualContainer)
        }
      }

      if (!actualContainer) {
        logger.error(
          'ERROR: No valid container found for WaveSurfer! Exiting initialization.'
        )
        alert('오디오 시각화 컨테이너를 찾을 수 없습니다.')
        return
      }

      // 새로운 WaveSurfer 인스턴스 생성
      waveSurferRef.current = WaveSurfer.create({
        container: actualContainer as HTMLElement,
        waveColor: isLiveMode ? '#f87171' : '#d1d5db',
        progressColor: '#f87171', // 라이브는 빨강, 재생은 파랑
        height: 80,
        barWidth: 6,
        barGap: 2,
        cursorColor: '#f87171',
        interact: !isLiveMode, // 라이브는 상호작용 비활성화, 재생은 활성화
        hideScrollbar: true,
        barRadius: 8,
        plugins: [recordPluginRef.current], // RecordPlugin 항상 플러그인에 포함
      })
      logger.info('WaveSurfer instance created.')

      // Canvas 요소 확인 로그
      if (container instanceof HTMLElement || container instanceof ShadowRoot) {
        const canvasElement = container.querySelector('canvas')
        if (canvasElement) {
          logger.info(
            'Canvas element found inside actual container:',
            canvasElement
          )
        } else {
          logger.error(
            'ERROR: No canvas element found immediately after WaveSurfer.create in actualContainer!'
          )
        }
      }

      waveSurferRef.current.on('error', (err: unknown) => {
        logger.error('오디오 처리 중 오류가 발생했습니다.')
      })

      // 재생 모드일 때만 필요한 이벤트 리스너 설정
      if (!isLiveMode) {
        waveSurferRef.current.on('ready', () => {
          if (waveSurferRef.current) {
            logger.info('waveSurferRef.current.getDuration()', {
              duration: waveSurferRef.current.getDuration(),
            })
            setDuration(waveSurferRef.current.getDuration()) // 오디오 총 길이 설정
          }
        })
        waveSurferRef.current.on('finish', () => {
          setIsPlayingRecorded(false) // 재생 완료 시 재생 상태 false
        })
        waveSurferRef.current.on('audioprocess', (time: number) => {})
        // 오디오 URL이 주어졌다면 로드
        if (audioUrlToLoad) {
          waveSurferRef.current.load(audioUrlToLoad)
          logger.info('Loading audio for playback:', {
            audioUrlToLoad: audioUrlToLoad,
          })
        }
      }
    },
    []
  ) // setupWaveSurfer 함수 자체는 의존성이 없습니다.

  // 1. 초기 마운트 시 WaveSurfer 설정 및 언마운트 시 정리
  useEffect(() => {
    logger.info(
      'Main useEffect: Component mounted, starting WaveSurfer initialization process.'
    )
    let timeoutId: NodeJS.Timeout

    const attemptInitialization = () => {
      logger.info('Attempting WaveSurfer initialization...')
      const containerElement = canvasRef.current
      let actualContainer: HTMLElement | ShadowRoot | null = null

      if (containerElement) {
        const shadowHostElement =
          containerElement.firstElementChild as HTMLElement | null
        if (shadowHostElement && shadowHostElement.shadowRoot) {
          actualContainer = shadowHostElement.shadowRoot
          logger.info('Container found: Shadow DOM (nested).', actualContainer)
        } else {
          actualContainer = containerElement
          logger.info('Container found: Light DOM element.', actualContainer)
        }
      }

      if (actualContainer && actualContainer.isConnected) {
        logger.info(
          'Container is connected to DOM. Initializing WaveSurfer for idle/playback.'
        )
        if (!waveSurferRef.current) {
          // 초기 마운트 시에는 재생 모드 (오디오 없음)로 초기화합니다.
          setupWaveSurfer(actualContainer, false)
        }
      } else {
        logger.warn(
          'Container not yet connected or not found. Retrying initialization in 100ms...'
        )
        timeoutId = setTimeout(attemptInitialization, 100)
      }
    }

    timeoutId = setTimeout(attemptInitialization, 50) // 50ms 후 첫 시도

    return () => {
      logger.info('Main useEffect cleanup: destroying WaveSurfer and plugins.')
      clearTimeout(timeoutId)
      if (waveSurferRef.current) {
        waveSurferRef.current.destroy()
        waveSurferRef.current = null
      }
      if (recordPluginRef.current) {
        recordPluginRef.current.destroy() // 플러그인 인스턴스도 정리
        recordPluginRef.current = null
      }
    }
  }, [setupWaveSurfer]) // setupWaveSurfer는 useCallback으로 안정화되어 있습니다.

  // 2. mode 또는 recordedAudioUrl 변경에 반응하여 WaveSurfer를 재설정하는 useEffect
  useEffect(() => {
    logger.info(`Mode or recordedAudioUrl changed. Current mode: ${mode}`)

    const containerElement = canvasRef.current
    let actualContainer: HTMLElement | ShadowRoot | null = null
    if (containerElement) {
      const shadowHostElement =
        containerElement.firstElementChild as HTMLElement | null
      if (shadowHostElement && shadowHostElement.shadowRoot) {
        actualContainer = shadowHostElement.shadowRoot
      } else {
        actualContainer = containerElement
      }
    }

    if (!actualContainer || !actualContainer.isConnected) {
      console.warn(
        'Secondary useEffect: Container not ready, skipping WaveSurfer setup.'
      )
      return
    }

    if (mode === 'recording') {
      logger.info(
        "Secondary useEffect: Mode is 'recording'. Setting up for live visualization."
      )
      setupWaveSurfer(actualContainer, true) // 라이브 시각화 모드로 설정
      const recorder = recordPluginRef.current

      setTimeout(() => {
        if (recorder && !recorder.isRecording()) {
          // 녹음이 성공적으로 시작되면 타이머 시작
          recorder
            .startRecording()
            .then(() => {
              setCurrentTime(0) // 시간 초기화
              intervalRef.current = setInterval(() => {
                setCurrentTime((prevTime) => prevTime + 1)
              }, 1000)
            })
            .catch((err) => {
              // ... 에러 처리
            })
        }
      }, 100)
    } else if (mode === 'playing_recorded' && recordedAudioUrl) {
      logger.info(
        "Secondary useEffect: Mode is 'playing_recorded' and audio available. Setting up for playback."
      )
      setupWaveSurfer(actualContainer, false, recordedAudioUrl) // 녹음된 오디오 재생 모드로 설정
    } else if (mode === 'idle' && recordedAudioUrl) {
      logger.info(
        "Secondary useEffect: Mode is 'idle' and recorded audio available. Setting up for playback (initial load after record-end)."
      )
      // 녹음 완료 후 idle 상태로 돌아왔을 때, 녹음된 오디오를 시각화합니다.
      setupWaveSurfer(actualContainer, false, recordedAudioUrl)
    } else if (mode === 'idle' && !recordedAudioUrl) {
      logger.info(
        "Secondary useEffect: Mode is 'idle' and no recorded audio. Setting up for idle state."
      )
      setupWaveSurfer(actualContainer, false) // 초기 idle 상태 또는 녹음/재생 후 오디오가 없을 때
    }
  }, [mode, recordedAudioUrl, setupWaveSurfer]) // mode, recordedAudioUrl, setupWaveSurfer가 변경될 때마다 실행

  // 마이크 녹음 및 시각화 시작 함수
  const startRecording = useCallback(async (): Promise<void> => {
    logger.info('startRecording called.')
    setCurrentTime(0)
    setFileDuration(null)
    setRecordedAudioUrl(null)
    try {
      setMode('recording') // 이 상태 업데이트가 위의 useEffect를 트리거하여 WaveSurfer를 라이브 모드로 설정합니다.
    } catch (err: unknown) {
      logger.error('마이크 접근 또는 녹음 시작 오류:', {
        err: JSON.stringify(err),
      })
      setMode('idle')
    }
  }, [])

  // 마이크 녹음 및 시각화 중지 함수
  const stopRecording = useCallback((): void => {
    logger.info('stopRecording called.')
    if (recordPluginRef.current && recordPluginRef.current.isRecording()) {
      recordPluginRef.current.stopRecording() // RecordPlugin으로 녹음 중지 (이것이 'record-end' 이벤트를 트리거합니다)
      cleanupInterval()
      logger.info('마이크 녹음 및 시각화 중지 요청 완료.')
    } else {
      logger.info('현재 녹음 중이 아닙니다.')
    }
  }, [cleanupInterval])

  // 녹음된 오디오 재생/일시정지 함수
  const toggleRecordedPlayback = useCallback((): void => {
    logger.info('toggleRecordedPlayback called.')
    if (!waveSurferRef.current || !recordedAudioUrl) {
      alert('녹음된 오디오가 없습니다. 먼저 녹음해주세요.')
      return
    }

    // 현재 모드가 'playing_recorded'가 아니라면 해당 모드로 전환하고 오디오 로드를 useEffect에 위임
    if (mode !== 'playing_recorded') {
      // 녹음 중이었다면 RecordPlugin을 중지합니다.
      if (recordPluginRef.current && recordPluginRef.current.isRecording()) {
        recordPluginRef.current.stopRecording()
      }
      setMode('playing_recorded')
    } else {
      if (waveSurferRef.current) {
        if (isPlayingRecorded) {
          waveSurferRef.current.pause()
          setIsPlayingRecorded(false)
        } else {
          // 현재 일시정지 중이라면 재생
          waveSurferRef.current.play()
          setIsPlayingRecorded(true)
        }

        setMode('playing_recorded')

        logger.info('Toggled playback:', {
          isPlayingRecorded: !isPlayingRecorded,
        })
      } else {
        logger.error(
          'WaveSurfer instance is null at togglePlay. This should not happen.'
        )
        setMode('idle')
      }
    }
  }, [mode, recordedAudioUrl, isPlayingRecorded])

  // 시간을 'MM:SS' 형식으로 포맷팅하는 헬퍼 함수
  const formatTime = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${minutes.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`
  }, [])

  // 훅이 반환하는 값들
  return {
    canvasRef,
    mode,
    startRecording,
    stopRecording,
    recordedAudioUrl,
    toggleRecordedPlayback,
    isPlayingRecorded,
    currentTime,
    duration,
    formatTime,
  }
}
